import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Grid,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  AudioFile as AudioFileIcon,
  VideoFile as VideoFileIcon,
  InsertDriveFile as FileIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { MediaAsset } from '../../../types/media.types';
import { createMediaAsset, updateMediaAsset } from '../slices/mediaSlice';

interface MediaFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (asset: MediaAsset) => void;
  media?: MediaAsset;
  isEdit: boolean;
  publisherId?: string;
}

const initialFormData = {
  name: '',
  description: '',
  publisherPK: '',
  mimeType: '',
  category: '',
  keywords: [] as string[],
};

const MediaForm: React.FC<MediaFormProps> = ({ open, onClose, onSuccess, media, isEdit, publisherId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [keywordInput, setKeywordInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (isEdit && media) {
      setFormData({
        name: media.name,
        description: media.description,
        publisherPK: media.publisherPK,
        mimeType: media.mimeType,
        category: media.category || '',
        keywords: media.keywords || [],
      });
      // Use publicUrl or s3Url from API response
      const mediaUrl = media.publicUrl || media.s3Url || media.fileUrl;
      if (mediaUrl) {
        setPreviewUrl(mediaUrl);
      }
    } else {
      const pk = publisherId ? `PUBLISHER#${publisherId}` : '';
      setFormData({ ...initialFormData, publisherPK: pk });
      setSelectedFile(null);
      setPreviewUrl('');
    }
    setErrors({});
    setCopySuccess(false);
  }, [open, isEdit, media, publisherId]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    
    setFormData({
      ...formData,
      mimeType: file.type,
      // Preserve the full filename with extension if name is empty
      // If user already has a name, keep it but we'll validate extension later
      name: formData.name || file.name,
    });

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else if (file.type.startsWith('audio/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl('');
    }

    if (errors.file) {
      setErrors({ ...errors, file: '' });
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, keywordInput.trim()],
      });
      setKeywordInput('');
    }
  };

  const handleDeleteKeyword = (keyword: string) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter((k) => k !== keyword),
    });
  };

  // Helper function to get extension from MIME type
  const getExtensionFromMimeType = (mimeType: string): string => {
    const mimeToExt: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'audio/mpeg': '.mp3',
      'audio/mp3': '.mp3',
      'audio/wav': '.wav',
      'audio/wave': '.wav',
      'audio/x-wav': '.wav',
      'audio/ogg': '.ogg',
      'audio/webm': '.webm',
      'audio/aac': '.aac',
      'video/mp4': '.mp4',
      'video/mpeg': '.mpeg',
      'video/webm': '.webm',
      'video/ogg': '.ogv',
      'video/quicktime': '.mov',
      'application/pdf': '.pdf',
    };
    return mimeToExt[mimeType.toLowerCase()] || '';
  };

  // Helper function to ensure filename has correct extension
  const ensureCorrectExtension = (filename: string, mimeType: string): string => {
    const expectedExt = getExtensionFromMimeType(mimeType);
    if (!expectedExt) return filename; // Unknown MIME type, don't modify
    
    // Check if filename already has an extension
    const currentExt = filename.match(/\.[^/.]+$/)?.[0] || '';
    
    // If no extension, add the correct one
    if (!currentExt) {
      return filename + expectedExt;
    }
    
    // If extension exists but doesn't match MIME type, replace it
    if (currentExt.toLowerCase() !== expectedExt.toLowerCase()) {
      return filename.replace(/\.[^/.]+$/, expectedExt);
    }
    
    return filename;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (selectedFile) {
      // Ensure the name has the correct extension based on MIME type
      const correctedName = ensureCorrectExtension(formData.name.trim(), formData.mimeType);
      if (correctedName !== formData.name.trim()) {
        // Auto-correct the name with proper extension
        setFormData({ ...formData, name: correctedName });
      }
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!isEdit && !selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      if (isEdit && media) {
        const result = await dispatch(
          updateMediaAsset({
            id: media.id,
            data: {
              ...formData,
              file: selectedFile || undefined,
            },
          })
        );
        if (updateMediaAsset.fulfilled.match(result) && onSuccess) {
          onSuccess(result.payload as MediaAsset);
        }
      } else {
        const result = await dispatch(
          createMediaAsset({
            ...formData,
            file: selectedFile!,
          })
        );
        if (createMediaAsset.fulfilled.match(result) && onSuccess) {
          onSuccess(result.payload as MediaAsset);
        }
      }
      onClose();
    } catch (error) {
      console.error('Error saving media asset:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    const publicUrl = media?.publicUrl || media?.s3Url || media?.fileUrl;
    if (!publicUrl) return;

    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const renderPreview = () => {
    if (!previewUrl) return null;

    const mimeType = formData.mimeType.toLowerCase();

    if (mimeType.startsWith('image/')) {
      return (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview
          </Typography>
          <Box
            component="img"
            src={previewUrl}
            alt="Preview"
            sx={{
              maxWidth: '100%',
              maxHeight: 300,
              objectFit: 'contain',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        </Box>
      );
    }

    if (mimeType.startsWith('audio/')) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview
          </Typography>
          <audio controls style={{ width: '100%' }}>
            <source src={previewUrl} type={formData.mimeType} />
            Your browser does not support the audio element.
          </audio>
        </Box>
      );
    }

    return null;
  };

  const getFileIcon = () => {
    const mimeType = formData.mimeType.toLowerCase();
    if (mimeType.startsWith('image/')) return <ImageIcon sx={{ fontSize: 48 }} />;
    if (mimeType.startsWith('audio/')) return <AudioFileIcon sx={{ fontSize: 48 }} />;
    if (mimeType.startsWith('video/')) return <VideoFileIcon sx={{ fontSize: 48 }} />;
    return <FileIcon sx={{ fontSize: 48 }} />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Media Asset' : 'Upload Media Asset'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* File Upload Area - Show for both new and edit modes */}
          <Grid item xs={12}>
            <Paper
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : errors.file ? 'error.main' : 'divider',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={handleBrowseClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleFileInputChange}
                accept="image/*,audio/*,video/*"
              />
              {selectedFile ? (
                <Box>
                  {getFileIcon()}
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  <Button size="small" sx={{ mt: 1 }} onClick={handleBrowseClick}>
                    Change File
                  </Button>
                  {isEdit && (
                    <Typography variant="caption" display="block" color="primary" sx={{ mt: 1 }}>
                      New file selected - will replace existing on save
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 48, color: 'action.active' }} />
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {isEdit ? 'Drop new file here to replace' : 'Drop file here or click to browse'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports images, audio, and video files
                  </Typography>
                  {isEdit && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Leave empty to keep current file
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
            {errors.file && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors.file}
              </Typography>
            )}
          </Grid>

          {/* Current/Preview - Show existing file in edit mode or new file preview */}
          {previewUrl && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {isEdit && !selectedFile ? 'Current File' : 'Preview'}
                </Typography>
                {renderPreview()}
              </Box>
            </Grid>
          )}

          {/* Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name || 'Filename with extension (e.g., logo.png)'}
              required
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              error={!!errors.description}
              helperText={errors.description}
              required
            />
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onChange={handleChange('category')}
              placeholder="e.g., Images, Audio, Documents"
            />
          </Grid>

          {/* MIME Type (read-only) */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="MIME Type"
              value={formData.mimeType}
              InputProps={{ readOnly: true }}
              disabled
              helperText={selectedFile ? 'Updated from new file' : 'From current file'}
            />
          </Grid>

          {/* Keywords */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Add Keywords"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddKeyword();
                }
              }}
              placeholder="Type a keyword and press Enter"
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {formData.keywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={keyword}
                  onDelete={() => handleDeleteKeyword(keyword)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Grid>

          {/* Copy URL Button */}
          {isEdit && media?.publicUrl && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyUrl}
                disabled={copySuccess}
              >
                {copySuccess ? 'Copied!' : 'Copy Public URL'}
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} /> : isEdit ? 'Update' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaForm;
